document.documentElement.classList.remove('no-js');

(function () {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let revealObserver = null;

  function initReveal(scope) {
    const root = scope || document;
    const items = root.querySelectorAll('.reveal, [data-reveal]');

    if (prefersReduced || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    }

    items.forEach((el, index) => {
      el.style.transitionDelay = `${Math.min(index * 45, 280)}ms`;
      revealObserver.observe(el);
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        window.requestAnimationFrame(() => el.classList.add('is-visible'));
      }
    });
  }

  function initMagnetic(scope) {
    const root = scope || document;
    root.querySelectorAll('.magnetic').forEach((el) => {
      if (el.dataset.magneticReady) return;
      el.dataset.magneticReady = 'true';
      el.addEventListener('mousemove', (event) => {
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${event.clientX - r.left}px`);
        el.style.setProperty('--my', `${event.clientY - r.top}px`);
      });
    });
  }

  function initMenu(scope) {
    const root = scope || document;
    const setSubmenuExpanded = (toggle, expanded) => {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      const label = toggle.querySelector('[data-submenu-label]');
      const menuName = toggle.dataset.submenuName || '';
      if (label) label.textContent = `${expanded ? 'Close' : 'Open'} ${menuName} menu`.replace(/\s+/g, ' ').trim();
    };
    root.querySelectorAll('[data-menu-toggle]').forEach((btn) => {
      if (btn.dataset.menuReady) return;
      btn.dataset.menuReady = 'true';
      const label = btn.querySelector('[data-menu-label]');
      btn.dataset.closedLabel = label ? label.textContent.trim() : 'Menu';
      btn.addEventListener('click', () => {
        const open = !document.body.classList.contains('menu-open');
        document.body.classList.toggle('menu-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (label) label.textContent = open ? 'Close' : btn.dataset.closedLabel;
        if (open) {
          const firstLink = document.querySelector('[data-site-navigation] a');
          if (firstLink) window.requestAnimationFrame(() => firstLink.focus());
        }
      });
    });

    root.querySelectorAll('[data-submenu-toggle]').forEach((btn) => {
      if (btn.dataset.submenuReady) return;
      btn.dataset.submenuReady = 'true';
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-nav-item]');
        const open = !item.classList.contains('submenu-open');
        item.parentElement.querySelectorAll('[data-nav-item].submenu-open').forEach((other) => {
          if (other !== item) {
            other.classList.remove('submenu-open');
            const otherToggle = other.querySelector('[data-submenu-toggle]');
            if (otherToggle) setSubmenuExpanded(otherToggle, false);
          }
        });
        item.classList.toggle('submenu-open', open);
        setSubmenuExpanded(btn, open);
      });
    });

    if (!document.documentElement.dataset.menuGlobalReady) {
      document.documentElement.dataset.menuGlobalReady = 'true';
      const closeMenu = (returnFocus) => {
        const wasOpen = document.body.classList.contains('menu-open');
        document.body.classList.remove('menu-open');
        document.querySelectorAll('[data-menu-toggle]').forEach((toggle) => {
          toggle.setAttribute('aria-expanded', 'false');
          const label = toggle.querySelector('[data-menu-label]');
          if (label) label.textContent = toggle.dataset.closedLabel || 'Menu';
          if (returnFocus && wasOpen) toggle.focus();
        });
        document.querySelectorAll('[data-nav-item].submenu-open').forEach((item) => item.classList.remove('submenu-open'));
        document.querySelectorAll('[data-submenu-toggle]').forEach((toggle) => setSubmenuExpanded(toggle, false));
      };

      document.addEventListener('click', (event) => {
        if (event.target.closest('[data-menu-close]')) closeMenu(false);
        if (window.innerWidth <= 900 && event.target.closest('[data-site-navigation] a')) closeMenu(false);
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeMenu(true);
        if (event.key !== 'Tab' || !document.body.classList.contains('menu-open')) return;
        const nav = document.querySelector('[data-site-navigation]');
        const toggle = document.querySelector('[data-menu-toggle]');
        if (!nav || !toggle) return;
        const focusable = Array.from(nav.querySelectorAll('a, button:not([disabled])')).filter((el) => el.offsetParent !== null);
        focusable.push(toggle);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }
  }

  function initHeroMotion(scope) {
    const root = scope || document;
    root.querySelectorAll('.hero').forEach((hero) => {
      const button = hero.querySelector('[data-hero-motion-toggle]');
      const videos = Array.from(hero.querySelectorAll('video'));
      if (!button || !videos.length || button.dataset.motionReady) return;
      button.dataset.motionReady = 'true';
      const label = button.querySelector('[data-hero-motion-label]');
      const sync = () => {
        const playing = videos.some((video) => !video.paused);
        if (label) label.textContent = playing ? 'Pause background video' : 'Play background video';
      };
      if (prefersReduced) videos.forEach((video) => video.pause());
      button.addEventListener('click', () => {
        const playing = videos.some((video) => !video.paused);
        videos.forEach((video) => {
          if (playing) video.pause();
          else video.play().catch(() => {});
        });
        window.setTimeout(sync, 0);
      });
      videos.forEach((video) => {
        video.addEventListener('play', sync);
        video.addEventListener('pause', sync);
      });
      sync();
    });
  }

  function initAll(scope) {
    initReveal(scope);
    initMagnetic(scope);
    initMenu(scope);
    initHeroMotion(scope);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initAll(document);
    if (prefersReduced) {
      document.querySelectorAll('video[autoplay]').forEach((video) => {
        video.removeAttribute('autoplay');
        video.pause();
      });
    }
  });
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const el = event.target;
    if (el && el.classList) el.classList.add('is-visible');
  });

  const header = document.querySelector('.site-header');
  window.addEventListener('scroll', () => {
    if (header) header.classList.toggle('is-scrolled', window.scrollY > 20);
    const progress = document.querySelector('.scroll-progress');
    if (progress) {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      progress.style.transform = `scaleX(${max ? window.scrollY / max : 0})`;
    }
  }, { passive: true });
})();
(function(){
  function initAccordion(scope){
    (scope || document).querySelectorAll('[data-accordion]').forEach((wrap)=>{
      if(wrap.dataset.accordionReady) return;
      wrap.dataset.accordionReady='true';
      wrap.querySelectorAll('.accordion-trigger').forEach((btn)=>{
        btn.addEventListener('click',()=>{
          const item=btn.closest('.accordion-item');
          const panel=item.querySelector('.accordion-panel');
          const open=item.classList.toggle('is-open');
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
          panel.style.maxHeight=open ? panel.scrollHeight + 'px' : '0px';
        });
      });
    });
  }
  function initTabs(scope){
    (scope || document).querySelectorAll('[data-tabs]').forEach((wrap)=>{
      if(wrap.dataset.tabsReady) return;
      wrap.dataset.tabsReady='true';
      const buttons=wrap.querySelectorAll('[data-tab]');
      const panels=wrap.querySelectorAll('[data-panel]');
      const activate=(button,moveFocus)=>{
        const id=button.dataset.tab;
        buttons.forEach((b)=>{
          const active=b===button;
          b.classList.toggle('is-active',active);
          b.setAttribute('aria-selected',active ? 'true' : 'false');
          b.tabIndex=active ? 0 : -1;
        });
        panels.forEach((p)=>{
          const active=p.dataset.panel===id;
          p.classList.toggle('is-active',active);
          p.hidden=!active;
        });
        if(moveFocus) button.focus();
      };
      buttons.forEach((button,index)=>{
        button.addEventListener('click',()=>activate(button,false));
        button.addEventListener('keydown',(event)=>{
          if(!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          event.preventDefault();
          let next=index;
          if(event.key==='ArrowLeft') next=(index-1+buttons.length)%buttons.length;
          if(event.key==='ArrowRight') next=(index+1)%buttons.length;
          if(event.key==='Home') next=0;
          if(event.key==='End') next=buttons.length-1;
          activate(buttons[next],true);
        });
      });
      if(buttons[0]) activate(buttons[0],false);
    });
  }
  function initExtra(scope){initAccordion(scope);initTabs(scope)}
  document.addEventListener('DOMContentLoaded',()=>initExtra(document));
  document.addEventListener('shopify:section:load',(event)=>initExtra(event.target));
})();


(function(){
  function initAxiomLinksAndProductGold(scope){
    const root = scope || document;

    // Do not hijack normal anchor behaviour. This simply repairs links where an overlay has captured the click.
    root.querySelectorAll('a.collection-tile, .shop-pills a, .category-strip a').forEach((link)=>{
      if(link.dataset.collectionLinkReady) return;
      link.dataset.collectionLinkReady = 'true';
      const href = link.getAttribute('href') || link.dataset.href;
      if(href && href !== '#') link.setAttribute('href', href);
    });

    root.querySelectorAll('.product-card').forEach((card)=>{
      if(card.dataset.goldReady) return;
      card.dataset.goldReady = 'true';
      card.addEventListener('mousemove', (event)=>{
        const r = card.getBoundingClientRect();
        card.style.setProperty('--mx', `${event.clientX - r.left}px`);
        card.style.setProperty('--my', `${event.clientY - r.top}px`);
      });
      card.addEventListener('click', ()=>{
        card.classList.add('is-clicked');
        window.setTimeout(()=>card.classList.remove('is-clicked'), 700);
      });
    });
  }
  document.addEventListener('DOMContentLoaded', ()=>initAxiomLinksAndProductGold(document));
  document.addEventListener('shopify:section:load', (event)=>initAxiomLinksAndProductGold(event.target));
})();


(function(){
  function scrollToProductsFromHash(){
    if(window.location.hash !== '#axiom-products') return;
    const target = document.getElementById('axiom-products');
    if(!target) return;
    window.setTimeout(()=>{
      target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      target.focus({ preventScroll: true });
    }, 180);
  }

  function initCollectionScrollLinks(scope){
    const root = scope || document;
    root.querySelectorAll('a[href*="#axiom-products"]').forEach((link)=>{
      if(link.dataset.productScrollReady) return;
      link.dataset.productScrollReady = 'true';
      link.addEventListener('click', (event)=>{
        const href = link.getAttribute('href');
        if(!href) return;
        const url = new URL(href, window.location.origin);
        if(url.origin === window.location.origin && url.pathname === window.location.pathname){
          const target = document.getElementById('axiom-products');
          if(target){
            event.preventDefault();
            history.replaceState(null, '', '#axiom-products');
            target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
            target.focus({ preventScroll: true });
          }
        }
      });
    });
  }

  document.addEventListener('DOMContentLoaded', ()=>{
    initCollectionScrollLinks(document);
    scrollToProductsFromHash();
  });
  document.addEventListener('shopify:section:load', (event)=>initCollectionScrollLinks(event.target));
  window.addEventListener('hashchange', scrollToProductsFromHash);
})();


(function(){
  function initVariantPricePanels(scope){
    const root = scope || document;
    root.querySelectorAll('[data-product-form]').forEach((form)=>{
      if(form.dataset.variantPriceReady) return;
      form.dataset.variantPriceReady = 'true';

      const container = form.closest('.product-info-sticky') || document;
      const select = form.querySelector('[data-product-variant-select]');
      const price = container.querySelector('[data-product-price]');
      const compare = container.querySelector('[data-product-compare-price]');
      const summary = container.querySelector('[data-product-variant-summary]');
      const sku = container.querySelector('[data-product-sku]');
      const availability = container.querySelector('[data-product-availability]');
      const addButton = form.querySelector('[data-product-add-button]');
      const dynamicCheckout = form.querySelector('.product-dynamic-checkout');

      function updateFromSelected(updateUrl){
        if(!select) return;
        const option = select.tagName === 'SELECT' ? select.options[select.selectedIndex] : select;
        if(!option) return;
        const available = option.dataset.available === 'true';

        if(price && option.dataset.price) price.textContent = option.dataset.price;
        if(compare){
          compare.textContent = option.dataset.comparePrice || '';
          compare.classList.toggle('is-hidden', option.dataset.compareActive !== 'true');
        }
        if(summary){
          summary.textContent = `${option.dataset.title || option.textContent}${available ? '' : ' · Sold out'}`;
        }
        if(sku){
          const value = option.dataset.sku || '';
          sku.textContent = value ? `SKU: ${value}` : '';
          sku.classList.toggle('is-hidden', !value);
        }
        if(availability){
          availability.textContent = available ? 'Available to order' : 'Currently unavailable';
          availability.classList.toggle('is-unavailable', !available);
        }
        if(addButton){
          addButton.disabled = !available;
          addButton.textContent = available ? (addButton.dataset.defaultLabel || 'Add to cart') : 'Sold out';
        }
        if(dynamicCheckout) dynamicCheckout.hidden = !available;
        if(updateUrl && option.value){
          const url = new URL(window.location.href);
          url.searchParams.set('variant', option.value);
          window.history.replaceState({}, '', url.toString());
        }
      }

      if(addButton && !addButton.dataset.defaultLabel) addButton.dataset.defaultLabel = addButton.textContent;
      if(select){
        select.addEventListener('change', () => updateFromSelected(true));
        updateFromSelected(false);
      }
    });
  }

  document.addEventListener('DOMContentLoaded',()=>initVariantPricePanels(document));
  document.addEventListener('shopify:section:load',(event)=>initVariantPricePanels(event.target));
})();

(function(){
  function initCollectionControls(scope){
    (scope || document).querySelectorAll('[data-collection-sort]').forEach((select)=>{
      if(select.dataset.sortReady) return;
      select.dataset.sortReady='true';
      select.addEventListener('change',()=>{
        const url=new URL(window.location.href);
        url.searchParams.set('sort_by',select.value);
        window.location.assign(url.toString());
      });
    });
  }
  document.addEventListener('DOMContentLoaded',()=>initCollectionControls(document));
  document.addEventListener('shopify:section:load',(event)=>initCollectionControls(event.target));
})();
